import {
  Component,
  ElementRef,
  computed,
  inject,
  linkedSignal,
  viewChildren
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import {
  CompatibilityConditionKind,
  compatibilityConditionKinds,
  compatibilityContextVocabulary,
  compatibilityVocabulary
} from '@core/filters/path-filter/vocabulary/compatibility.vocabulary';
import { RoutePosition } from '@core/filters/path-filter/wire';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { EntityDisplayNamesService } from '@core/services/entity-display-names/entity-display-names.service';
import { ICONS } from '@core/services/icons/icons.service';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { PathFilterComposerComponent } from '@shared/components/path-filter/composer/path-filter-composer.component';
import { DslTextComponent } from '@shared/components/path-filter/dsl-text/dsl-text.component';
import { PathFilterPanelComponent } from '@shared/components/path-filter/panel/path-filter-panel.component';
import { PathFilterPhraseComponent } from '@shared/components/path-filter/phrase/path-filter-phrase.component';
import { AvailableLanguages } from '@shared/types/translation.type';
import { nextRadioIndex } from '@shared/utils/radio-group-keys';

import { targetDisplayName } from './compatibility-display';
import { CompatibilityEditSessionService } from './compatibility-edit-session.service';
import {
  RequirementDraft,
  ServiceWindow,
  decodeRequirement,
  encodeRequirement,
  newRequirementDraft,
  printConditionText
} from './requirement-draft';
import { describeRequirement } from './requirement-phrase';

interface TypeOption {
  soft: boolean;
  label: string;
}

@Component({
  selector: 'app-compatibility-requirement-panel',
  imports: [
    TranslatePipe,
    CheckboxComponent,
    DslTextComponent,
    PathFilterComposerComponent,
    PathFilterPanelComponent,
    PathFilterPhraseComponent
  ],
  templateUrl: './compatibility-requirement-panel.component.html',
  host: { class: 'flex h-full w-[432px] shrink-0' }
})
export class CompatibilityRequirementPanelComponent {
  protected readonly session = inject(CompatibilityEditSessionService);
  private readonly displayNames = inject(EntityDisplayNamesService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly typeRadios = viewChildren<ElementRef<HTMLButtonElement>>('typeRadio');
  protected readonly ICONS = ICONS;

  protected readonly typeOptions: readonly TypeOption[] = [
    { soft: false, label: 'form.compatibility.hard' },
    { soft: true, label: 'form.compatibility.panel.soft' }
  ];

  protected readonly view = computed(() => {
    const request = this.session.request();
    if (!request) {
      return null;
    }
    const kinds = new Map<string, CompatibilityConditionKind>(
      compatibilityConditionKinds(request.carrier).map((kind) => [kind.kind, kind])
    );
    return {
      selfLabel: request.selfLabel,
      targetLabel: targetDisplayName(
        request.target,
        this.displayNames.singularDisplayNames()
      ),
      vocabulary: compatibilityVocabulary(request.carrier, request.target),
      contextVocabulary: compatibilityContextVocabulary(request.carrier),
      takesConditions: kinds.size > 0,
      context: kinds.get('context') ?? null,
      position: kinds.get('route_position') ?? null,
      serviceWindow: kinds.get('service_time_window') ?? null
    };
  });

  /** The whole requirement in one sentence, in place of a phrase per composer. */
  protected readonly phrase = computed(() => {
    const view = this.view();
    const draft = this.draft();
    if (!view || !draft) return [];
    return describeRequirement(
      draft,
      { filter: view.vocabulary, context: view.contextVocabulary },
      this.currentLang() as AvailableLanguages,
      this.translate,
      view.selfLabel
    );
  });

  /** Rebuilt from the session whenever a card opens a requirement; unapplied edits are dropped. */
  protected readonly draft = linkedSignal<RequirementDraft | null>(() => {
    const request = this.session.request();
    if (!request) {
      return null;
    }
    return request.requirement
      ? decodeRequirement(request.requirement, request.carrier)
      : newRequirementDraft(request.target);
  });

  protected readonly problem = computed(() => {
    const draft = this.draft();
    if (!draft) {
      return null;
    }
    const encoded = encodeRequirement(draft);
    return encoded.ok ? null : encoded.problem;
  });

  protected readonly keptTexts = computed(() =>
    (this.draft()?.kept ?? []).map((entry) => printConditionText(entry.condition))
  );

  /**
   * What the service-window inputs show. A window the carrier does not enforce is kept as stored
   * rather than decoded, but the disabled inputs still show it until it is removed from the list.
   * Editable inputs never show a kept window: typing there writes a new one beside it.
   */
  protected readonly shownWindow = computed<ServiceWindow | null>(() => {
    const draft = this.draft();
    if (!draft || draft.serviceWindow || this.view()?.serviceWindow?.enforceable) {
      return draft?.serviceWindow ?? null;
    }
    const kept = draft.kept.find(
      (entry) => entry.condition.kind === 'service_time_window'
    )?.condition;
    return kept?.kind === 'service_time_window'
      ? { start: kept.start, end: kept.end }
      : null;
  });

  protected readonly positionOptions = computed(() =>
    (this.view()?.position?.positions ?? []).map((position) => ({
      position,
      toggle: (checked: boolean) => this.togglePosition(position, checked)
    }))
  );

  protected update(patch: Partial<RequirementDraft>): void {
    this.draft.update((draft) => (draft ? { ...draft, ...patch } : draft));
  }

  protected onTypeKeydown(event: KeyboardEvent, index: number): void {
    const next = nextRadioIndex(event.key, index, this.typeOptions.length);
    if (next === null) return;
    event.preventDefault();
    this.typeRadios()[next]?.nativeElement.focus();
    this.update({ soft: this.typeOptions[next].soft });
  }

  protected setPenalty(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.update({ penalty: raw === '' ? null : Number(raw) });
  }

  protected setWindow(part: keyof ServiceWindow, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const current = this.draft()?.serviceWindow ?? { start: '', end: '' };
    this.update({ serviceWindow: { ...current, [part]: value } });
  }

  protected removeKept(index: number): void {
    const draft = this.draft();
    if (draft) {
      this.update({ kept: draft.kept.filter((_, i) => i !== index) });
    }
  }

  protected apply(): void {
    const draft = this.draft();
    if (!draft) {
      return;
    }
    const encoded = encodeRequirement(draft);
    if (encoded.ok) {
      this.session.apply(encoded.requirement);
    }
  }

  private togglePosition(position: RoutePosition, checked: boolean): void {
    const offered = this.view()?.position?.positions ?? [];
    const selected = new Set(this.draft()?.positions ?? []);
    if (checked) {
      selected.add(position);
    } else {
      selected.delete(position);
    }
    this.update({ positions: offered.filter((option) => selected.has(option)) });
  }
}
